import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { Link, useNavigate, useParams } from "react-router-dom";
import MainThemeLayout from "../../../layout/MainThemeLayout";
import Input from "../../../components/Input";
import SelectMultiple from "../../../components/SelectMultiple";
import PrevButton from "../../../components/PrevButton";
import { validateData, getOrDeleteRequest, postOrPutRequest, encryptData } from "../../../utils/utils";

const AddOrEditCompany = () => {
    const { id } = useParams();
    const [loading, setLoading] = useState(id ? true : false);
    const [companyTypes, setCompanyTypes] = useState([]);
    const [businessSectors, setBusinessSectors] = useState([]);
    const [projects, setProjects] = useState([]);
    const [editPassword, setEditPassword] = useState(!id ? true : false);
    const navigate = useNavigate();

    const [form, setForm] = useState({
        company_name: "",
        email: "",
        password: "",
        password_confirmation: "",
        company_types: [],
        projects: [],
        businessSectors: [],
        contact_name: "",
        contact_phone: "",
        latitude: "",
        longitude: "",
    });

    const [alert, setAlert] = useState([{
        inputName: "",
        status: false,
        message: ""
    }]);

    const getData = async (company) => {
        const response = await getOrDeleteRequest(`company/find/${company}`, 'GET');
        if (response.type === 'success') {
            setForm({
                company_name: response.data.company_name,
                email: response.data.email,
                company_types: response.data.company_types.map((type) => type.id),
                projects: response.data.projects.map((project) => project.id),
                businessSectors: response.data.business_sectors[0].id,
                contact_name: response.data.contact_name,
                contact_phone: response.data.contact_phone,
                latitude: response.data.latitude,
                longitude: response.data.longitude,
                password: "",
                password_confirmation: "",
            });
        }
    }

    const getCompanyTypes = async () => {
        const response = await getOrDeleteRequest(`company-type/filter`, 'GET');
        if (response.type === 'success') setCompanyTypes(response.data);
    };

    const getBusinessSectors = async () => {
        const response = await getOrDeleteRequest(`business_sectors/filter`, 'GET');
        if (response.type === 'success') setBusinessSectors(response.data);
    };

    const getProjects = async () => {
        const response = await getOrDeleteRequest(`projects/filter`, 'GET');
        if (response.type === 'success') setProjects(response.data);
    };

    const validateCoordinates = (latitude, longitude) => {
        if (isNaN(latitude) || isNaN(longitude)) {
            return { valid: false, message: "Las coordenadas deben ser números válidos" };
        }

        const lat = parseFloat(latitude);
        const lng = parseFloat(longitude);

        if (lat < -90 || lat > 90) {
            return { valid: false, message: "La latitud debe estar entre -90 y 90 grados" };
        }

        if (lng < -180 || lng > 180) {
            return { valid: false, message: "La longitud debe estar entre -180 y 180 grados" };
        }

        return { valid: true, message: "" };
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (form.latitude || form.longitude) {
            const coordValidation = validateCoordinates(form.latitude, form.longitude);
            if (!coordValidation.valid) {
                Swal.fire({ title: '¡Advertencia!', text: coordValidation.message, icon: 'warning' });
                return;
            }
        }

        const inputs = Object.entries(form)
            .filter(([key]) => key !== "latitude" && key !== "longitude" && editPassword && (key === "password" || key === "password_confirmation"))
            .map(([key, value]) => ({ name: key, value }));

        const datosValidados = validateData(inputs);
        setAlert(datosValidados);
        let totalValidaciones = datosValidados.filter((input) => input.status !== false).map(() => { return false });

        if (totalValidaciones.length === 0) {
            Swal.fire({ title: 'Cargando...', text: 'Por favor espere', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
            sendData(form);
        }
    };

    const sendData = async (data) => {
        const info = { 
            ...data,
            email: encryptData(data.email),
            business_sectors: [data.businessSectors],
            district_id: id ? undefined : 1
        };

        if (editPassword) {
            info.password = encryptData(data.password);
            info.password_confirmation = encryptData(data.password_confirmation);
        } else {
            delete info.password;
            delete info.password_confirmation;
        }
        
        delete info.businessSectors;

        const response = await postOrPutRequest(
            id ? `company/update/${id}?_method=PUT` : `company/create`,
            'POST',
            info
        );

        if (response.type === 'success') {
            Swal.fire({ title: '¡Éxito!', text: `Organización ${id ? 'editada' : 'agregada'} exitosamente`, icon: 'success' })
                .then(() => navigate('/Organizaciones'));
        } else {
            Swal.fire({ title: response.message, text: 'Verifique su información', icon: 'warning' });
        }
    };

    const loadData = async () => {
        setLoading(true);
        await Promise.all([getCompanyTypes(), getBusinessSectors(), getProjects()]);
        if (id) await getData(id);
        setLoading(false);
    };

    useEffect(() => { loadData(); }, []);

    return (
        <MainThemeLayout>
            <div className="px-8 py-6">
                <div>
                    <Link to="/Organizaciones"><PrevButton className="relative !top-0 py-2 !left-2" /></Link>
                </div>
                
                <div className="mb-4">
                    <h1 className="text-xl font-semibold text-stone-800 dark:text-white mb-2">
                        {id ? "Actualizando información de la" : "Agregando nueva"} empresa o entidad
                    </h1>
                    <p className="text-sm text-stone-500 dark:text-white">
                        Por favor complete los campos para {id ? "actualizar la" : "agregar una nueva"} empresa o entidad. 
                        Los campos marcados con (<span className="text-red-500">*</span>) son obligatorios.
                    </p>
                </div>

                <div className="bg-white dark:bg-[#292929] px-6 py-4 rounded-lg">
                    <form onSubmit={handleSubmit}>
                        <div className="flex flex-wrap">
                            <div className="w-full px-2 pb-4">
                                <Input
                                    type="text"
                                    label="Nombre de la empresa o entidad"
                                    name="company_name"
                                    loading={loading}
                                    alert={alert}
                                    required
                                    onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                                    tooltip="Nombre de la empresa o entidad"
                                    value={form.company_name}
                                />
                            </div>

                            <div className="w-full md:w-1/2 px-2 pb-4">
                                <SelectMultiple
                                    label="Tipo de empresa o entidad"
                                    name="company_types"
                                    loading={loading}
                                    alert={alert}
                                    array={companyTypes}
                                    required
                                    onChange={(e) => setForm({ ...form, company_types: [e] })}
                                    tooltip="Tipo de empresa o entidad (Ejemplo: Pública, Privada, ONG, etc.)"
                                    value={form.company_types}
                                />
                            </div>

                            <div className="w-full md:w-1/2 px-2 pb-4">
                                <SelectMultiple
                                    label="Sector de la empresa o entidad"
                                    name="businessSectors"
                                    loading={loading}
                                    alert={alert}
                                    array={businessSectors}
                                    required
                                    onChange={(e) => setForm({ ...form, businessSectors: e })}
                                    tooltip="Sector de la empresa o entidad (Ejemplo: Salud, Educación, etc.)"
                                    value={form.businessSectors}
                                />
                            </div>

                            <div className="w-full px-2 pb-4">
                                <SelectMultiple
                                    label="Proyectos a los que pertenece la empresa o entidad"
                                    name="projects"
                                    loading={loading}
                                    alert={alert}
                                    array={projects}
                                    required
                                    isMulti
                                    value={form.projects}
                                    onChange={(e) => setForm({ ...form, projects: e })}
                                    tooltip="Proyectos a los que pertenece la empresa o entidad"
                                />
                            </div>

                            <div className="w-full md:w-1/2 px-2 pb-4">
                                <Input
                                    type="text"
                                    label="Nombre de contacto"
                                    name="contact_name"
                                    loading={loading}
                                    alert={alert}
                                    required
                                    onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                                    tooltip="Nombre de contacto"
                                    value={form.contact_name}
                                />
                            </div>

                            <div className="w-full md:w-1/2 px-2 pb-4">
                                <Input
                                    type="text"
                                    label="Teléfono de contacto"
                                    mask="9999-9999"
                                    name="contact_phone"
                                    loading={loading}
                                    alert={alert}
                                    required
                                    onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                                    tooltip="Teléfono de contacto"
                                    value={form.contact_phone}
                                />
                            </div>

                            <div className="w-full px-2 pb-4">
                                <Input
                                    type="text"
                                    label="Correo electrónico de la empresa o entidad"
                                    name="email"
                                    loading={loading}
                                    alert={alert}
                                    required
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    tooltip="Correo electrónico de la empresa o entidad"
                                    value={form.email}
                                />
                            </div>

                            <div className="w-full md:w-1/2 px-2 pb-4">
                                <Input
                                    type="text"
                                    label="Latitud"
                                    name="latitude"
                                    loading={loading}
                                    alert={alert}
                                    onChange={(e) => setForm({ ...form, latitude: e.target.value })}
                                    value={form.latitude}
                                    tooltip="Latitud de la empresa o entidad (Información geográfica)"
                                />
                            </div>

                            <div className="w-full md:w-1/2 px-2 pb-4">
                                <Input
                                    type="text"
                                    label="Longitud"
                                    name="longitude"
                                    loading={loading}
                                    alert={alert}
                                    onChange={(e) => setForm({ ...form, longitude: e.target.value })}
                                    value={form.longitude}
                                    tooltip="Longitud de la empresa o entidad (Información geográfica)"
                                />
                            </div>

                            {id && <div className="w-full">
                                <div className="flex items-center justify-between mb-2">
                                    <label className="text-sm text-stone-700 dark:text-white">
                                        {editPassword ? "Contraseña de la empresa o entidad" : "Editar contraseña de la empresa o entidad"}
                                    </label>
                                    <button
                                        type="button"
                                        className="text-xs text-blue-500 hover:underline"
                                        onClick={() => setEditPassword(!editPassword)}
                                    >
                                        {editPassword ? "Cancelar edición" : "Editar contraseña"}
                                    </button>
                                </div>
                                <p className="text-xs text-stone-500 dark:text-white mb-2">
                                    {editPassword ? "Ingrese la contraseña de la empresa o entidad." : "Si desea editar la contraseña, haga clic en 'Editar contraseña'."}
                                </p>
                            </div>}

                            {editPassword && (
                                <>
                                    <div className="w-full md:w-1/2 px-2 pb-4">
                                        <Input
                                            type="password"
                                            label="Contraseña de la empresa o entidad"
                                            name="password"
                                            loading={loading}
                                            alert={alert}
                                            required
                                            onChange={(e) => setForm({ ...form, password: e.target.value })}
                                            tooltip="Contraseña de la empresa o entidad"
                                        />
                                    </div>
                                    <div className="w-full md:w-1/2 px-2 pb-4">
                                        <Input
                                            type="password"
                                            label="Confirmar contraseña de la empresa o entidad"
                                            name="password_confirmation"
                                            loading={loading}
                                            alert={alert}
                                            required
                                            onChange={(e) => setForm({ ...form, password_confirmation: e.target.value })}
                                            tooltip="Confirmar contraseña de la empresa o entidad"
                                        />
                                    </div>
                                </>
                            )}
                        </div>

                        <div className="flex justify-center">
                            <button className="bg-turquesa text-white rounded-md px-4 py-2 text-xs sm:text-sm focus:outline-none focus-visible:shadow-outline">
                                Guardar
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </MainThemeLayout>
    );
};

export default AddOrEditCompany;